import { Component, ChangeDetectionStrategy, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SidebarNavItemComponent } from './sidebar-nav-item.component';
import { NavConfigEntry, NavItem, NavSection, isNavSection } from './sidebar-nav.types';

/**
 * Shared navigation item list component.
 *
 * Renders a flat list of primary NavItems followed by labeled NavSections.
 * In collapsed mode (sidebar icon-only state), section labels are hidden and
 * replaced by a visual separator line.
 *
 * Used by both SidebarNavComponent and MobileMenuDialogComponent to ensure
 * consistent rendering across contexts.
 */
@Component({
  selector: 'zag-nav-item-list',
  imports: [TranslatePipe, SidebarNavItemComponent],
  template: `
    <!-- Primary items (no section label) -->
    @for (item of primaryItems(); track item.id) {
      <zag-sidebar-nav-item [item]="item" [collapsed]="collapsed()" />
    }

    <!-- Sectioned items -->
    @for (section of sections(); track section.label) {
      @if (collapsed()) {
        <!-- Collapsed: show a visual separator instead of the label -->
        <li class="zag-nav-item-list__separator" aria-hidden="true"></li>
      } @else {
        <!-- Expanded: show section label -->
        <li class="zag-nav-item-list__section-header" aria-hidden="true">
          <span class="zag-nav-item-list__section-label">{{ section.label | translate }}</span>
        </li>
      }
      @for (item of section.items; track item.id) {
        <zag-sidebar-nav-item [item]="item" [collapsed]="collapsed()" />
      }
    }
  `,
  styleUrl: './nav-item-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-nav-item-list]': 'true',
    '[class.zag-nav-item-list--collapsed]': 'collapsed()',
  },
})
export class NavItemListComponent {
  /** All nav config entries (primary items + sections mixed) */
  readonly entries = input.required<NavConfigEntry[]>();

  /** Whether the sidebar is in collapsed (icon-only) mode */
  readonly collapsed = input<boolean>(false);

  /** Primary items extracted from entries (non-section NavItems) */
  readonly primaryItems = (): NavItem[] => this.entries().filter((e): e is NavItem => !isNavSection(e));

  /** Sections extracted from entries */
  readonly sections = (): NavSection[] => this.entries().filter((e): e is NavSection => isNavSection(e));
}
